import { DOCUMENT } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ContentLinksComponent } from '@app/components/content-links/content-links.component';
import { ParagraphPgPnav } from '@app/models/paragraph.model';
import { TextComponent } from '@app/components/text/text.component';
import { Icon, Url } from '@app/models/global.model';
import { IconCtaComponent } from '@app/components/icon-cta/icon-cta.component';
import { ComponentSharedModule } from '@app/modules/component.shared.module';
import { ApiService } from '@app/services/api.service';

@Component({
  selector: 'mobi-hero-text',
  templateUrl: './hero-text.component.html',
  styleUrls: ['./hero-text.component.scss'],
  standalone: true,
  imports: [
    ComponentSharedModule,
    ContentLinksComponent,
    TextComponent,
    IconCtaComponent,
  ],
})
export class HeroTextComponent {
  readonly superTitle = input<string>();
  readonly subtitle = input<string>();
  readonly title = input<string>();
  readonly lead = input<string>();
  readonly contentLinks = input<ParagraphPgPnav[] | undefined>();
  readonly otherLinks = input<Url[]>([]);
  readonly isCentered = input<boolean>(false);
  readonly fullWidth = input<boolean>(false);
  readonly primaryCta = input<string>();
  readonly primaryCtaTitle = input<string>();
  readonly primaryCtaTarget = input<string>();
  readonly primaryCtaIcon = input<Icon>();
  readonly secondaryCta = input<string>();
  readonly secondaryCtaTitle = input<string>();
  readonly secondaryCtaLabel = input<string>();
  readonly secondaryCtaTarget = input<string>();
  readonly secondaryCtaIcon = input<Icon>();

  readonly sanitizedSuperTitle = computed<SafeHtml>(() =>
    this.#highlightConsecutiveSpaces(this.superTitle())
  );
  readonly sanitizedSubtitle = computed<SafeHtml>(() =>
    this.#highlightConsecutiveSpaces(this.subtitle())
  );
  readonly sanitizedTitle = computed<SafeHtml>(() =>
    this.#highlightConsecutiveSpaces(this.title())
  );

  #api = inject(ApiService);
  #doc = inject(DOCUMENT);
  #sanitizer = inject(DomSanitizer);

  #highlightConsecutiveSpaces(value: string | undefined): SafeHtml {
    const container = this.#doc.createElement('span');
    container.textContent = value ?? '';
    if (!this.#api.loggedIn()) {
      return this.#sanitizer.bypassSecurityTrustHtml(container.innerHTML);
    }

    const walker = this.#doc.createTreeWalker(container, 4);
    const textNodes: Text[] = [];
    let node: Node | null;

    while ((node = walker.nextNode())) {
      textNodes.push(node as Text);
    }

    textNodes.forEach((textNode) => {
      const value = textNode.textContent ?? '';
      if (
        !/[ \u00a0]{2,}/.test(value) ||
        textNode.parentElement?.closest('.mobi-consecutive-spaces')
      ) {
        return;
      }

      const fragment = this.#doc.createDocumentFragment();
      value.split(/([ \u00a0]{2,})/).forEach((part) => {
        if (/^[ \u00a0]{2,}$/.test(part)) {
          const marker = this.#doc.createElement('span');
          marker.className = 'mobi-consecutive-spaces';
          marker.textContent = part;
          fragment.appendChild(marker);
          return;
        }

        fragment.appendChild(this.#doc.createTextNode(part));
      });
      textNode.replaceWith(fragment);
    });

    return this.#sanitizer.bypassSecurityTrustHtml(container.innerHTML);
  }
}
